import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import extractUrlsWithIndices from "twitter-text/dist/extractUrlsWithIndices";

export const linkMenuPluginKey = new PluginKey("link-menu");

function getWordBoundsAtPosition(str, position) {
  const isSpace = (c) => /\s/.exec(c);
  let start = position - 1;
  let end = position;

  while (start >= 0 && !isSpace(str[start])) {
    start -= 1;
  }
  start = Math.max(0, start + 1);

  while (end < str.length && !isSpace(str[end])) {
    end += 1;
  }
  end = Math.max(start, end);

  return {
    start,
    end,
  };
}

const getNewState = () => ({
  active: false,
  range: {},
  text: "",
});

export const linkMenuPlugin = ({
  onEnter = () => false,
  onExit = () => false,
}) =>
  new Plugin({
    key: linkMenuPluginKey,
    view() {
      return {
        update: (view, prevState) => {
          const prev = this.key.getState(prevState);
          const next = this.key.getState(view.state);
          const node = this.props.getNode(view);

          // See how the state changed
          const stopped = prev.active && !next.active;
          const changed =
            prev.active !== next.active ||
            prev.text !== next.text ||
            prev.range !== next.range;

          // Trigger the hooks when necessary
          if (stopped) {
            onExit({
              view,
              node,
              range: prev.range,
              text: prev.text,
            });
          } else if (changed) {
            onEnter({
              view,
              node,
              range: next.range,
              text: next.text,
            });
          }
        },
      };
    },
    state: {
      init() {
        return getNewState();
      },
      apply(tr) {
        const newState = getNewState();
        const { selection } = tr;

        if (selection.from !== selection.to || !selection.empty) {
          return newState;
        }

        const $position = selection.$from;
        const cursorPosition = $position.pos;

        tr.doc.descendants((node, position) => {
          if (newState.active || !node.text) {
            return;
          }

          const { text } = node;
          const positionEnd = position + text.length;

          if (position > cursorPosition || positionEnd < cursorPosition) {
            return;
          }

          const wordPosition = getWordBoundsAtPosition(
            text,
            cursorPosition - position
          );
          const word = text.substring(wordPosition.start, wordPosition.end);
          const isLink = extractUrlsWithIndices(word)?.[0]?.url === word;

          if (word && isLink) {
            newState.active = true;
            newState.range = {
              from: position + wordPosition.start,
              to: position + wordPosition.end,
            };
            newState.text = word;
          }
        });

        return newState;
      },
    },
    props: {
      getNode(view) {
        const node = view?.domAtPos(view.state.selection.$from.pos)?.node;
        const selector = 'span[data-mark][data-type="link-menu"]';

        return (
          node?.querySelector?.(selector) ||
          node?.parentNode?.closest?.(selector)
        );
      },
      decorations(editorState) {
        const { active, range } = this.getState(editorState);

        if (!active) return null;

        return DecorationSet.create(editorState.doc, [
          Decoration.inline(range.from, range.to, {
            nodeName: "span",
            "data-mark": "",
            "data-type": "link-menu",
          }),
        ]);
      },
    },
  });
